import { createSelector } from '@reduxjs/toolkit';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as DashboardsAPI from '../api/dashboardsAPI';
import { NEW_DASHBOARD_ID } from '../ui/constants';
import type { Dashboard } from './types';

interface State {
  editingDashboard: {
    data: Dashboard;
    status: 'idle' | 'dirty';
    savingStatus: DataStatus;
  } | null;
  dashboards:
    | {
        data: Dashboard[];
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
}

const initialState: State = {
  editingDashboard: null,
  dashboards: {
    data: null,
    status: DataStatus.idle,
  },
};

export const dashboardsSlice = createAppSlice({
  name: 'dashboards',
  initialState,
  reducers: (create) => ({
    setupDashboards: create.asyncThunk<void, Dashboard[]>(
      () => {
        return DashboardsAPI.getDashboards();
      },
      {
        pending: (state) => {
          state.dashboards.status = DataStatus.loading;
        },
        rejected: (state) => {
          state.dashboards.status = DataStatus.error;
          state.dashboards.data = null;
        },
        fulfilled: (state, action) => {
          state.dashboards.data = action.payload;
          state.dashboards.status = DataStatus.finished;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (
              isLoading(state.dashboards.dashboards.status) ||
              isFinished(state.dashboards.dashboards.status)
            ) {
              return false;
            }
          },
        },
      },
    ),
    createNewDashboard: create.reducer((state) => {
      state.editingDashboard = {
        data: {
          id: NEW_DASHBOARD_ID,
          name: 'Untitled dashboard',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          favorite: false,
          widgets: [],
        },
        status: 'idle',
        savingStatus: DataStatus.idle,
      };
    }),
    editDashboard: create.reducer<Dashboard>((state, action) => {
      state.editingDashboard = {
        data: action.payload,
        status: 'idle',
        savingStatus: DataStatus.idle,
      };
    }),
    updateEditingDashboard: create.reducer<Partial<Dashboard>>(
      (state, action) => {
        state.editingDashboard!.data = {
          ...state.editingDashboard!.data,
          ...(action.payload as Dashboard),
        };
        state.editingDashboard!.status = 'dirty';
      },
    ),
    saveDashboard: create.asyncThunk<
      Pick<Dashboard, 'id' | 'name' | 'widgets'>,
      Dashboard
    >(
      async ({ id, name, widgets }) => {
        if (id === NEW_DASHBOARD_ID) {
          return DashboardsAPI.createDashboard({
            name,
            widgets,
            favorite: false,
          });
        }
        return DashboardsAPI.updateDashboard({ id, name, widgets });
      },
      {
        pending: (state) => {
          state.editingDashboard!.savingStatus = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          const savedDashboard = action.payload;
          state.editingDashboard!.data = savedDashboard;
          state.editingDashboard!.status = 'idle';
          state.editingDashboard!.savingStatus = DataStatus.finished;

          if (state.dashboards.data) {
            const existingIndex = state.dashboards.data.findIndex(
              (d) => d.id === savedDashboard.id,
            );
            if (existingIndex >= 0) {
              state.dashboards.data[existingIndex] = savedDashboard;
            } else {
              state.dashboards.data.unshift(savedDashboard);
            }
          }
        },
        rejected: (state) => {
          state.editingDashboard!.savingStatus = DataStatus.error;
        },
      },
    ),
    toggleFavorite: create.asyncThunk<Dashboard, Dashboard>(
      async (dashboard) => {
        return DashboardsAPI.updateDashboard({
          id: dashboard.id,
          favorite: !dashboard.favorite,
        });
      },
      {
        fulfilled: (state, action) => {
          const updated = action.payload;
          if (state.dashboards.data) {
            const index = state.dashboards.data.findIndex(
              (d) => d.id === updated.id,
            );
            if (index >= 0) {
              state.dashboards.data[index] = updated;
            }
          }
        },
      },
    ),
    removeDashboard: create.asyncThunk<string, void>(
      (id) => {
        return DashboardsAPI.deleteDashboard(id);
      },
      {
        fulfilled: (state, action) => {
          if (state.dashboards.data) {
            state.dashboards.data = state.dashboards.data.filter(
              (d) => d.id !== action.meta.arg,
            );
          }
        },
      },
    ),
    duplicateDashboard: create.asyncThunk<Dashboard, Dashboard>(
      async (dashboard) => {
        return DashboardsAPI.createDashboard({
          name: `${dashboard.name} (copy)`,
          widgets: dashboard.widgets,
          favorite: false,
        });
      },
      {
        fulfilled: (state, action) => {
          if (state.dashboards.data) {
            state.dashboards.data.unshift(action.payload);
          }
        },
      },
    ),
    cancelEditingDashboard: create.reducer((state) => {
      state.editingDashboard = null;
    }),
    reset: create.reducer(() => {
      return { ...initialState };
    }),
  }),
  selectors: {
    selectDashboards: createSelector(
      (state: State) => state.dashboards.data,
      (data) => {
        return data || [];
      },
    ),
    selectIsDashboardsLoaded: (state) => {
      return isFinished(state.dashboards.status);
    },
    selectIsDashboardsLoading: (state) => {
      return isLoading(state.dashboards.status);
    },
    selectDashboardsDataStatus: (state) => {
      return state.dashboards.status;
    },
    selectEditingDashboard: (state) => {
      return state.editingDashboard;
    },
    selectIsEditingDashboard: (state) => {
      return !!state.editingDashboard;
    },
    selectIsEditingDashboadrHasUnsavedChanges: (state) => {
      return state.editingDashboard?.status === 'dirty';
    },
    selectIsEditingDashboardNew: (state) => {
      return state.editingDashboard?.data.id === NEW_DASHBOARD_ID;
    },
    selectIsEditingDashboardSaving: (state) => {
      return state.editingDashboard?.savingStatus === DataStatus.loading;
    },
  },
});

export const {
  selectDashboards,
  selectDashboardsDataStatus,
  selectIsDashboardsLoaded,
  selectIsDashboardsLoading,
  selectEditingDashboard,
  selectIsEditingDashboard,
  selectIsEditingDashboadrHasUnsavedChanges,
  selectIsEditingDashboardNew,
  selectIsEditingDashboardSaving,
} = dashboardsSlice.selectors;
export const {
  setupDashboards,
  reset,
  createNewDashboard,
  updateEditingDashboard,
  editDashboard,
  cancelEditingDashboard,
  saveDashboard,
  toggleFavorite,
  removeDashboard,
  duplicateDashboard,
} = dashboardsSlice.actions;
